import SessionResource from 'App/resources/Session';
import SelfResource from 'App/resources/Self';
import history from 'App/pages/routes/history';
import { LoginErrors } from 'App/pages/Login/LoginError';
import config from '../../config';
import { passwordHelper } from '@upsales/common';
import { removeAllNotifications } from 'Store/reducers/SystemNotificationReducer';
import InAppChat from 'Services/InAppChat';
import store, { RESET_ALL_REDUCERS, RootState } from 'Store/index';
import { initSession, setLoading } from 'Store/actions/AppActions';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import AppService from 'App/legacy/AppService';
import { isExtensionPanel, postSsoLoginToPanel } from 'App/pages/EmbedAIChat/panelContext';
import { clearAgentCookie } from 'App/helpers/agentCookieHelper';
import { clearSessionIdentity } from 'App/helpers/sessionSync';
import logError from 'Helpers/logError';
import CacheService from 'App/services/CacheService';
import { reloadWithDebugInfoIfNeeded } from 'App/helpers/angularDebugInfo';
import EventBus from 'App/services/EventBus';
import StateRouter from 'App/services/StateRouter';

export const getEnvString = () => {
	let envString = '';
	if (config.ENV === 'DEV' || config.ENV === 'ALPHA' || config.ENV === 'BETA') {
		envString = config.ENV;

		if (config.ENV === 'BETA' && config.URL.indexOf('sandbox') !== -1) {
			envString = 'SANDBOX';
		}
	}
	return envString;
};

export const checkBrowser = () => {
	const supportedBrowsers = ['Chrome', 'Firefox'];
	const browser = navigator.userAgent;

	const mobile = ['Android', 'iPhone'];
	let unsupported = true,
		isMobileBrowser = false,
		mobileType = null;

	for (let i = 0; i < supportedBrowsers.length; i++) {
		if (browser.includes(supportedBrowsers[i])) {
			unsupported = false;
			break;
		}
	}

	for (let i = 0; i < mobile.length; i++) {
		if (browser.includes(mobile[i])) {
			isMobileBrowser = true;
			mobileType = mobile[i].toLowerCase();
			break;
		}
	}

	return { unsupported, mobileType, isMobileBrowser };
};

// A single malformed-`%` cookie (e.g. a third-party `foo=50%off`) would otherwise
// make decodeURIComponent throw and take down all cookie parsing — including the
// login screen's getSavedUser and consumeLoginReturnUrl. Fall back to the raw value.
const safeDecodeURIComponent = (value: string): string => {
	try {
		return decodeURIComponent(value);
	} catch {
		return value;
	}
};

const parseKeyValueArray = (array: string[]) =>
	array.reduce<{ [k: string]: any }>((res, item) => {
		const trimmed = item.trim();
		const eqIdx = trimmed.indexOf('=');
		const key = safeDecodeURIComponent(eqIdx === -1 ? trimmed : trimmed.slice(0, eqIdx));
		if (eqIdx === -1) {
			return { ...res, [key]: undefined };
		}
		const val = safeDecodeURIComponent(trimmed.slice(eqIdx + 1));
		try {
			return { ...res, [key]: JSON.parse(val) };
		} catch {
			return { ...res, [key]: val };
		}
	}, {});

const parseCookies = () => parseKeyValueArray(document.cookie.split(';'));

export const getSavedUser = () => {
	const savedUserEmailKey = 'ls.Upsales.crm.savedEmail';
	const savedUserNameKey = 'ls.Upsales.crm.savedName';
	const cookies = parseCookies();

	if (cookies[savedUserEmailKey] && cookies[savedUserNameKey]) {
		return { email: cookies[savedUserEmailKey], name: cookies[savedUserNameKey] };
	}

	return null;
};

const deleteCookie = (key: string) => {
	document.cookie = `${key}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};

export const setSavedUser = (email: string | null, name: string | null) => {
	if (email && name) {
		document.cookie = `ls.Upsales.crm.savedEmail=${email}; path=/; expires=Fri, 31 Dec 9999 23:59:59 GMT`;
		document.cookie = `ls.Upsales.crm.savedName=${name}; path=/; expires=Fri, 31 Dec 9999 23:59:59 GMT`;
	} else {
		deleteCookie('ls.Upsales.crm.savedEmail');
		deleteCookie('ls.Upsales.crm.savedName');
	}
};

const DEV_SAVED_CREDS_KEY = 'Upsales.dev.savedCreds';

export const getDevSavedCreds = (): { email: string; password: string } | null => {
	if (config.ENV !== 'DEV') return null;
	const raw = localStorage.getItem(DEV_SAVED_CREDS_KEY);
	if (!raw) return null;
	try {
		const parsed = JSON.parse(raw);
		if (typeof parsed?.email === 'string' && typeof parsed?.password === 'string') {
			return { email: parsed.email, password: parsed.password };
		}
	} catch (e) {
		logError(e, 'Failed to parse dev saved creds');
	}
	return null;
};

export const setDevSavedCreds = (email: string, password: string) => {
	if (config.ENV !== 'DEV') return;
	localStorage.setItem(DEV_SAVED_CREDS_KEY, JSON.stringify({ email, password }));
};

const getUrlParams = () => parseKeyValueArray(history.location.search.replace('?', '').split('&'));

export const checkIsMobileServiceAppLogin = () => {
	const { isMobileServiceAppLogin } = getUrlParams();

	return isMobileServiceAppLogin;
};

// The deep-link the user was heading to before being bounced to login.
export const getLoginReturnUrl = (): string | null => {
	const { loginReturnUrl } = parseCookies();
	if (!loginReturnUrl || loginReturnUrl === 'undefined') {
		return null;
	}
	// The cookie is client-writable, so a planted value is treated as untrusted:
	// only navigate to a same-origin http(s) URL, never javascript:/data:/cross-origin.
	try {
		const parsed = new URL(loginReturnUrl, window.location.origin);
		return parsed.origin === window.location.origin ? parsed.href : null;
	} catch {
		return null;
	}
};

// Reads and clears the deep-link. Consumed by the `tmpStart` state's controller so the
// post-login checks (AcceptTerms/DelayedPayment/onboarding) still run before landing on the link.
export const consumeLoginReturnUrl = (): string | null => {
	const returnUrl = getLoginReturnUrl();
	deleteCookie('loginReturnUrl');
	return returnUrl;
};

export const redirectToStartpage = async (customerId: number) => {
	const hash = getUrlParams();

	if (hash.externalSSO) {
		return StateRouter.go('externalSSO', { site: hash.externalSSO });
	} else if (hash.returnTo) {
		let params = { customerId };

		if (hash.params) {
			params = hash.params;
		}
		return StateRouter.go(hash.returnTo, params).catch(() => StateRouter.go('tmpStart', { customerId }));
	} else {
		return StateRouter.go('tmpStart', { customerId });
	}
};

const onSuccessfullLogin = async (rememberUser: boolean) => {
	let self;
	try {
		self = (await SelfResource.get()).data;
	} catch (error: any) {
		const res = error.response?.data;
		const missingIndex = res?.error?.key === 'MissingIndex';
		const isTrial = res?.client?.provisioningTrial;
		if (missingIndex && isTrial) {
			throw LoginErrors.MISSING_INDEX_TRIAL;
		} else if (missingIndex) {
			throw LoginErrors.MISSING_INDEX;
		}
		throw LoginErrors.UNKNOWN;
	}

	if (rememberUser) {
		setSavedUser(self.email, self.name);
	} else {
		setSavedUser(null, null);
	}

	// Start init the app
	const dispatch = store.dispatch as ThunkDispatch<RootState, any, AnyAction>;
	if (await dispatch(initSession(true))) {
		return;
	}

	// Redirect to startpage
	await redirectToStartpage(self.clients[0].clientId);

	if (reloadWithDebugInfoIfNeeded(store.getState().App.scripts)) {
		return;
	}

	// We hide the loader when the startpage has been loaded. Give it some extra time
	setTimeout(() => dispatch(setLoading(false)));
};

const onSuccessfullMobileLogin = (token?: string) => {
	window.location.replace(`upsalesservice://?token=${token}`);
};

export const login = async (email: string, password: string, rememberUser: boolean) => {
	let token;
	const isMobileServiceAppLogin = checkIsMobileServiceAppLogin();
	try {
		const hash = getUrlParams();
		const { data } = await SessionResource.login({
			email,
			password,
			samlBypass: hash.samlBypass || null,
			skipCookie: isMobileServiceAppLogin,
			serviceApp: isMobileServiceAppLogin
		});
		token = data.token;
		if (data.isTwoFactorAuth) {
			return { token, isTwoFactorAuth: true };
		}
	} catch (error: any) {
		const res = error.response?.data;
		const status = error.response?.status;
		if (status === 403 && res.errorCode === 113) {
			throw LoginErrors.NO_VALID_BRAND;
		} else if (status === 403 && res.errorCode === 140) {
			throw LoginErrors.ACCOUNT_LOCKED;
		} else if (status === 403) {
			throw LoginErrors.IP_BLOCKED;
		}
		// if error is 402 there is no active agreement
		else if (status === 402) {
			throw res.key === 'TrialEnded' ? LoginErrors.TRIAL_EXPIRED : LoginErrors.ACCOUNT_EXPIRED;
		} else if (status === 401 && res.error?.errorCode === 94) {
			throw LoginErrors.NO_PHONE_2FA;
		} else if (status === 401 && res.error?.key === 'SamlRequired') {
			// The credentials were valid but the account must log in through SSO — take the
			// user to the IdP instead of showing "invalid credentials". Happens when the
			// client-side SSO check didn't run or failed before submit. WEB-7980.
			if (res.ssoUrl) {
				// In the Chrome panel the IdP can't be framed, so hand it to the panel to open top-level
				if (isExtensionPanel()) {
					postSsoLoginToPanel(res.ssoUrl);
				} else {
					window.location.href = res.ssoUrl;
				}
			}
			throw LoginErrors.SAML_REQUIRED;
		} else if (res.errorCode === 116) {
			throw LoginErrors.FORCE_RESET_PASSWORD;
		} else if (res.errorCode === 171) {
			throw LoginErrors.USER_INACTIVE;
		} else {
			// else just show error invalid pass/username
			throw LoginErrors.CREDENTIALS;
		}
	}

	if (isMobileServiceAppLogin) {
		onSuccessfullMobileLogin(token);
		return;
	}
	await onSuccessfullLogin(rememberUser);

	return { isTwoFactorAuth: false, token };
};

export const getResetHash = () => {
	const params = getUrlParams();

	return (params.resetPassword as string) || null;
};

export const getActivateFortnox = () => {
	const params = getUrlParams();

	return !!params.activateFortnox;
};

export const getTwoFaKey = () => {
	const params = getUrlParams();

	return (params.twoFaKey as string) || null;
};

const passwordValidationErrorMap: { [key: string]: LoginErrors } = {
	'resetPassword.rule.atleastEightCharactersError': LoginErrors.PASSWORD_TOO_SHORT,
	'resetPassword.rule.containCapitalAndNonCapitalError': LoginErrors.PASSWORD_CAPITAL,
	'resetPassword.rule.containNumberError': LoginErrors.PASSWORD_NUMBER
};

export const validatePassword = (password: string, password2: string): null | LoginErrors | LoginErrors[] => {
	// eslint-disable-next-line security/detect-possible-timing-attacks
	if (password !== password2) {
		return LoginErrors.PASSWORDS_NOT_MATCHING;
	}

	const validation = passwordHelper.validate(password);

	if (!validation.valid) {
		return validation.errors.map(error => {
			return passwordValidationErrorMap[error] || LoginErrors.UNKNOWN;
		});
	}

	return null;
};

export const ensureUserIsLoggedOut = async () => {
	// A logged out tab owns no identity, so it must stop mirroring the browser's session:
	// otherwise the next account to sign in anywhere reloads this tab, and the reload runs
	// this teardown again, logging that brand new session out. WEB-10004.
	clearSessionIdentity();

	return new Promise<void>(resolve => {
		SessionResource.logout()
			.finally(function () {
				// this is still needed to tell some angular components to clear internal variables
				EventBus.broadcast('upsales.logout');

				// Destroy push notification socket
				Tools.PushNotifications.destroy();

				// logout zendesk user
				if (window.zE) {
					window.zE('messenger', 'logoutUser');
				}

				// Clear all notifications
				removeAllNotifications();

				// Shutdown in app chat
				InAppChat.shutdown();

				// Clear cache
				CacheService.clear();

				// Clear getTrialAddons cache from local storage
				localStorage.removeItem('trialCache');

				// Cross-tab logout signal: same-origin surfaces that aren't this Angular
				// instance (e.g. the Chrome side-panel iframe) listen for this `storage`
				// event and return to the login screen. WEB-7101.
				try {
					localStorage.setItem('upsales:logout', String(Date.now()));
				} catch {
					/* localStorage unavailable — non-fatal */
				}

				// Run AppService.reset to clear the session timeout
				AppService.reset();

				// Remove upsales-auth cookie
				clearAgentCookie();

				// Reset redux state
				store.dispatch({ type: RESET_ALL_REDUCERS });

				resolve();
			})
			.catch(() => {
				// We do not care if logout fails
			});
	});
};

export const loginTwoFA = async (token: string, code: string, rememberUser: boolean) => {
	try {
		const isMobileServiceAppLogin = checkIsMobileServiceAppLogin();
		await SessionResource.loginTwoFA(token, code, isMobileServiceAppLogin);
	} catch (error: any) {
		const res = error.response?.data;

		if (res?.key === 'TwoFactorCodeWrong') {
			throw LoginErrors.INVALID_2FA;
		}
		if (res?.key === 'TwoFactorCodeDisabled') {
			throw LoginErrors.OUTDATED_2FA;
		}
		throw LoginErrors.UNKNOWN;
	}

	await onSuccessfullLogin(rememberUser);
};

export const getLoginMethod = async (email: string, noRedirect = false, rememberUser = false) => {
	const hash = getUrlParams();
	const isMobileServiceAppLogin = checkIsMobileServiceAppLogin();

	return SessionResource.getLoginMethod(
		email,
		hash.samlBypass || null,
		noRedirect,
		isMobileServiceAppLogin,
		rememberUser
	);
};
